import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowUp, BookOpen, Check, Code, Copy, Layers, ListChecks, MessageCircleQuestion, PanelRightClose, Sparkles, Square,
  SquarePen, ThumbsDown, ThumbsUp, X,
} from 'lucide-react';
import { Button } from '../ui';
import { Markdown } from '../ui/Markdown';
import { useAuth } from '../../contexts/AuthContext';
import { aiErrorInfo, aiService } from '../../services/api';
import { AIState, blockedState, stateOfError } from './AIStates';
import { ActiveReview } from './ActiveReview';
import { useAIStatus } from './useAIStatus';
import './AITutor.css';

const STEPS = ['Lendo o material...', 'Procurando no resumo...', 'Escrevendo a resposta...'];
const HISTORY_MAX = 40;

/** Separa a linha "Fontes: [Resumo §2] [Transcrição 04:12]" do fim da resposta. */
export function splitSources(text) {
  const m = /\n*\s*Fontes?:\s*((?:\[[^\]]+\]\s*)+)\s*$/i.exec(text || '');
  if (!m) return { body: text || '', sources: [] };
  const sources = [...m[1].matchAll(/\[([^\]]+)\]/g)].map(x => x[1].trim());
  return { body: text.slice(0, m.index).trimEnd(), sources };
}

const storageKey = (userId, ctx) => `ss-tutor:${userId}:${ctx.lessonId ? `l${ctx.lessonId}` : `t${ctx.topicId}`}`;

/**
 * Tutor fixo na lateral da aula ou do tópico (2a). Já sabe o material (o servidor monta o contexto),
 * guarda a conversa, responde em streaming com fontes e permite Parar.
 *
 * ctx: { lessonId } ou { topicId }. reading: o que o tutor lê ("esta aula · resumo e transcrição").
 * request: { message, selection, mode } vindo de fora (seleção de texto, botões da página).
 */
export function AITutor({ ctx, title, reading, open, onOpenChange, request, onSource, hasCode }) {
  const { user } = useAuth();
  const [status, refreshStatus] = useAIStatus(open && !user?.demo);
  const [messages, setMessages] = useState(() => {
    try { return JSON.parse(localStorage.getItem(storageKey(user.id, ctx)) || '[]'); } catch { return []; }
  });
  const [input, setInput] = useState('');
  const [selection, setSelection] = useState(null);
  const [streaming, setStreaming] = useState(false);
  const [step, setStep] = useState(0);
  const [error, setError] = useState(null);
  const [view, setView] = useState('chat');
  const [cards, setCards] = useState(null);
  const abort = useRef(null);
  const listRef = useRef(null);
  const lastRequest = useRef(null);
  const ctxKey = storageKey(user.id, ctx);

  // Troca de aula: carrega a conversa daquela aula
  useEffect(() => {
    try { setMessages(JSON.parse(localStorage.getItem(ctxKey) || '[]')); } catch { setMessages([]); }
    setError(null);
    setView('chat');
    setCards(null);
  }, [ctxKey]);

  useEffect(() => {
    try { localStorage.setItem(ctxKey, JSON.stringify(messages.slice(-HISTORY_MAX))); } catch { /* sem storage */ }
  }, [messages, ctxKey]);

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, streaming, cards, error]);

  useEffect(() => {
    if (!streaming) return undefined;
    const t = setInterval(() => setStep(s => Math.min(STEPS.length - 1, s + 1)), 1800);
    return () => clearInterval(t);
  }, [streaming]);

  useEffect(() => () => abort.current?.abort(), []);

  // Aberto, o conteúdo da página abre espaço à direita; o painel começa abaixo do topo (e do subnav do curso)
  const [top, setTop] = useState(null);
  useEffect(() => {
    document.body.classList.toggle('has-ai-tutor', open);
    if (!open) return undefined;
    const header = document.querySelector('.ss-topbar');
    const measure = () => setTop(header ? Math.max(0, header.getBoundingClientRect().bottom) : null);
    measure();
    window.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);
    return () => {
      document.body.classList.remove('has-ai-tutor');
      window.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
    };
  }, [open]);

  const send = useCallback(async (text, sel = selection) => {
    const content = (text || '').trim();
    if (!content || streaming) return;
    setError(null);
    setCards(null);
    const userMsg = { role: 'user', content, selection: sel || undefined, at: Date.now() };
    const history = [...messages, userMsg];
    setMessages([...history, { role: 'assistant', content: '', pending: true }]);
    setInput('');
    setSelection(null);
    setStreaming(true);
    setStep(0);
    lastRequest.current = { content, sel };
    const controller = new AbortController();
    abort.current = controller;
    let acc = '';
    try {
      const done = await aiService.tutorStream(
        { ...ctx, messages: history.map(m => ({ role: m.role, content: m.content })), selection: sel || undefined },
        {
          signal: controller.signal,
          onDelta: (d) => {
            acc += d;
            setMessages(list => {
              const copy = list.slice();
              copy[copy.length - 1] = { role: 'assistant', content: acc, pending: true };
              return copy;
            });
          },
        },
      );
      setMessages(list => {
        const copy = list.slice();
        if (!acc.trim()) return copy.slice(0, -1);
        copy[copy.length - 1] = {
          role: 'assistant', content: acc, usageId: done?.usageId, viaOwnKey: done?.keySource === 'USER', stopped: done?.stopped,
        };
        return copy;
      });
      refreshStatus();
    } catch (err) {
      const info = aiErrorInfo(err);
      setMessages(list => {
        const copy = list.slice();
        // Mantém o que já chegou; sem nada, tira o balão vazio (a pergunta fica)
        if (acc.trim()) copy[copy.length - 1] = { role: 'assistant', content: acc, stopped: true };
        else copy.pop();
        return copy;
      });
      setError(info);
      if (info.code === 'AI_DAILY_LIMIT' || info.code === 'AI_NOT_CONFIGURED') refreshStatus();
    } finally {
      setStreaming(false);
      abort.current = null;
    }
  }, [ctx, messages, selection, streaming, refreshStatus]);

  const retry = () => {
    const r = lastRequest.current;
    if (!r) return;
    // A pergunta ficou salva no histórico: reenvia sem duplicar
    setMessages(list => (list[list.length - 1]?.role === 'user' ? list.slice(0, -1) : list));
    setTimeout(() => send(r.content, r.sel), 0);
  };

  // Pedidos de fora: seleção de texto ("Explicar", "Me pergunte") e botões da página
  useEffect(() => {
    if (!request) return;
    onOpenChange(true);
    if (request.mode === 'review') {
      setView('review');
      return;
    }
    if (request.mode === 'flashcards') {
      setView('chat');
      draftCards(request.selection, 1);
      return;
    }
    setView('chat');
    if (request.selection) setSelection(request.selection);
    if (request.message) send(request.message, request.selection || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request]);

  const draftCards = async (text, count = 5) => {
    setError(null);
    setCards({ loading: true, items: [] });
    try {
      const r = await aiService.draftFlashcards({ ...ctx, text: text || undefined, count });
      setCards({ loading: false, items: r.cards, saved: false });
      refreshStatus();
    } catch (err) {
      setCards(null);
      setError(aiErrorInfo(err));
    }
  };

  const saveCards = async () => {
    try {
      await aiService.saveFlashcards({ ...ctx, cards: cards.items, source: 'tutor' });
      setCards(c => ({ ...c, saved: true }));
    } catch (err) {
      setError(aiErrorInfo(err));
    }
  };

  const clear = () => {
    abort.current?.abort();
    setMessages([]);
    setError(null);
    setCards(null);
  };

  const blocked = blockedState(status) || (user?.demo ? 'DEMO' : null);
  const quick = useMemo(() => [
    { icon: MessageCircleQuestion, label: 'Me pergunte', run: () => setView('review') },
    { icon: ListChecks, label: 'Resumir em 5 pontos', run: () => send('Resuma esta aula em 5 pontos, com os termos da prova em negrito.') },
    { icon: Layers, label: 'Gerar flashcards', run: () => draftCards(null, 5) },
    ...(hasCode ? [{ icon: Code, label: 'Explicar o código', run: () => send('Explique os blocos de código do material, linha por linha, e o que pode dar errado.') }] : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [hasCode, send]);

  const used = status?.messagesToday ?? 0;
  const limit = status?.messagesLimit ?? 0;
  const nearLimit = limit > 0 && used / limit >= 0.8;

  if (!open) {
    return (
      <button type="button" className="ai-tutor-fab" onClick={() => onOpenChange(true)} aria-label="Abrir o tutor">
        <Sparkles size={18} aria-hidden="true" /><span>Tutor</span>
        {messages.length > 0 && <span className="ai-tutor-fab__dot" aria-hidden="true" />}
      </button>
    );
  }

  return (
    <aside className="ai-tutor" aria-label="Tutor" style={top != null ? { '--ai-tutor-top': `${top}px` } : undefined}>
      <header className="ai-tutor__head">
        <span className="ai-tutor__icon"><Sparkles size={17} aria-hidden="true" /></span>
        <span className="ai-tutor__title">
          <strong>Tutor</strong>
          <span>Lendo: {reading}</span>
        </span>
        <button type="button" className="ss-iconbtn" onClick={clear} title="Nova conversa" aria-label="Nova conversa" disabled={!messages.length}>
          <SquarePen size={16} aria-hidden="true" />
        </button>
        <button type="button" className="ss-iconbtn" onClick={() => onOpenChange(false)} title="Recolher" aria-label="Recolher o tutor">
          <PanelRightClose size={16} aria-hidden="true" />
        </button>
      </header>

      {view === 'review' && !blocked ? (
        <ActiveReview ctx={ctx} title={title} status={status} onBack={() => setView('chat')} onSource={onSource} onUsage={refreshStatus} />
      ) : (
        <>
          <div className="ai-tutor__list" ref={listRef}>
            {blocked && <AIState kind={blocked} status={status} />}
            {!blocked && messages.length === 0 && !cards && (
              <div className="ai-tutor__empty">
                <strong>Pergunte sobre {title}</strong>
                <span>O tutor já leu o material. Selecione um trecho da página para explicar, ser perguntado ou virar flashcard.</span>
              </div>
            )}
            {messages.map((m, i) => (
              m.role === 'user'
                ? <UserBubble key={i} m={m} />
                : <AssistantMessage key={i} m={m} ctx={ctx} onSource={onSource} onCards={(text) => draftCards(text, 1)} streaming={streaming && i === messages.length - 1} />
            ))}
            {streaming && messages[messages.length - 1]?.content === '' && (
              <div className="ai-thinking">
                <span className="ai-dots" aria-hidden="true"><i /><i /><i /></span>{STEPS[step]}
                <button type="button" className="ai-stop" onClick={() => abort.current?.abort()}><Square size={11} aria-hidden="true" />Parar</button>
              </div>
            )}
            {cards && <CardsDraft cards={cards} onSave={saveCards} onClose={() => setCards(null)} />}
            {error && <AIState kind={stateOfError(error.code)} status={status} message={error.message} onRetry={stateOfError(error.code) === 'ERROR' ? retry : undefined} compact />}
          </div>

          <div className="ai-tutor__foot">
            {!blocked && (
              <div className="ai-tutor__quick">
                {quick.map(q => (
                  <button key={q.label} type="button" onClick={q.run} disabled={streaming}>
                    <q.icon size={13} aria-hidden="true" />{q.label}
                  </button>
                ))}
              </div>
            )}
            {selection && (
              <div className="ai-tutor__sel">
                <span>«{selection.length > 120 ? `${selection.slice(0, 120)}…` : selection}»</span>
                <button type="button" className="ss-iconbtn" onClick={() => setSelection(null)} aria-label="Tirar o trecho"><X size={13} aria-hidden="true" /></button>
              </div>
            )}
            <form className="ai-tutor__input" onSubmit={(e) => { e.preventDefault(); send(input); }}>
              <textarea
                rows={1}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(input); } }}
                placeholder={`Pergunte sobre ${ctx.lessonId ? 'esta aula' : 'este tópico'}...`}
                disabled={!!blocked}
                aria-label="Pergunta para o tutor"
              />
              {streaming ? (
                <button type="button" className="ai-send is-stop" onClick={() => abort.current?.abort()} aria-label="Parar"><Square size={14} aria-hidden="true" /></button>
              ) : (
                <button type="submit" className="ai-send" disabled={!input.trim() || !!blocked} aria-label="Enviar"><ArrowUp size={16} aria-hidden="true" /></button>
              )}
            </form>
            <span className={`ai-tutor__disclaimer ${nearLimit ? 'is-near' : ''}`}>
              A IA pode errar. Confira com o material da aula.
              {limit > 0 && !status?.viaUserKey && ` · ${used} de ${limit} mensagens hoje`}
              {status?.viaUserKey && ' · via sua chave'}
            </span>
          </div>
        </>
      )}
    </aside>
  );
}

function UserBubble({ m }) {
  return (
    <div className="ai-msg ai-msg--user">
      {m.selection && <blockquote>“{m.selection.length > 220 ? `${m.selection.slice(0, 220)}…` : m.selection}”</blockquote>}
      <p>{m.content}</p>
    </div>
  );
}

function AssistantMessage({ m, onSource, onCards, streaming }) {
  const { body, sources } = useMemo(() => splitSources(m.content), [m.content]);
  const [copied, setCopied] = useState(false);
  const [vote, setVote] = useState(m.vote || 0);
  if (!m.content) return null;
  const copy = async () => {
    try { await navigator.clipboard.writeText(body); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* sem clipboard */ }
  };
  const feedback = (v) => {
    const next = vote === v ? 0 : v;
    setVote(next);
    if (m.usageId) aiService.feedback(m.usageId, next).catch(() => {});
  };
  return (
    <div className="ai-msg ai-msg--ai">
      <span className="ai-msg__avatar"><Sparkles size={13} aria-hidden="true" /></span>
      <div className="ai-msg__main">
        <Markdown className={`ai-md ${streaming ? 'is-streaming' : ''}`}>{body}</Markdown>
        {sources.length > 0 && (
          <div className="ai-msg__sources">
            {sources.map(s => (
              <button key={s} type="button" className="ai-source" onClick={() => onSource?.(s)}>
                <BookOpen size={12} aria-hidden="true" />{s.replace(/\s*§/, ' · §').replace(/^Transcrição\s+/, 'Transcrição · ')}
              </button>
            ))}
          </div>
        )}
        {!m.pending && (
          <div className="ai-msg__actions">
            <button type="button" onClick={() => onCards(body)}><Layers size={13} aria-hidden="true" />Virar flashcard</button>
            <button type="button" onClick={copy}>{copied ? <Check size={13} aria-hidden="true" /> : <Copy size={13} aria-hidden="true" />}{copied ? 'Copiado' : 'Copiar'}</button>
            {m.viaOwnKey && <span className="ai-msg__via">via sua chave</span>}
            {m.stopped && <span className="ai-msg__via">interrompida</span>}
            <span className="ai-msg__spacer" />
            <button type="button" className={vote === 1 ? 'is-on' : ''} onClick={() => feedback(1)} aria-label="Ajudou"><ThumbsUp size={13} aria-hidden="true" /></button>
            <button type="button" className={vote === -1 ? 'is-on' : ''} onClick={() => feedback(-1)} aria-label="Não ajudou"><ThumbsDown size={13} aria-hidden="true" /></button>
          </div>
        )}
      </div>
    </div>
  );
}

function CardsDraft({ cards, onSave, onClose }) {
  return (
    <div className="ai-cards">
      <div className="ai-cards__head">
        <Layers size={14} aria-hidden="true" /><span>{cards.loading ? 'Criando flashcards...' : `${cards.items.length} ${cards.items.length === 1 ? 'flashcard' : 'flashcards'}`}</span>
        <button type="button" className="ss-iconbtn" onClick={onClose} aria-label="Fechar"><X size={13} aria-hidden="true" /></button>
      </div>
      {cards.loading ? (
        <div className="ai-thinking"><span className="ai-dots" aria-hidden="true"><i /><i /><i /></span>Escolhendo os pontos...</div>
      ) : (
        <>
          <ul>
            {cards.items.map((c, i) => <li key={i}><strong>{c.front}</strong><span>{c.back}</span></li>)}
          </ul>
          <Button size="sm" icon={cards.saved ? Check : Layers} onClick={onSave} disabled={cards.saved}>
            {cards.saved ? 'Nos seus flashcards' : 'Salvar nos meus flashcards'}
          </Button>
        </>
      )}
    </div>
  );
}
